import React, { useMemo } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Flex,
  Text,
  Spinner,
  Button,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { ArrowSquareOut, Copy } from 'phosphor-react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { useNotasDuplicadas } from '../../../../hooks/useNotasDuplicadas';
import { IEmpresaCard } from '../../../../hooks/useConferenciaFiscal';
import { GruposDuplicadas } from '../duplicateNotes/GruposDuplicadas';

interface IRange {
  data_ini?: string;
  data_fim?: string;
}

/**
 * Duplicadas da empresa a partir do card da Conferência. Mesma visão da tela de
 * Notas Duplicadas (GruposDuplicadas): grupo inteiro, motivo e o que muda entre
 * os lançamentos — quem olha por aqui vê exatamente o que a tela mostra.
 */
export const DuplicadasDrawer = ({
  empresa,
  range,
  onClose,
}: {
  empresa: IEmpresaCard | null;
  range: IRange;
  onClose: () => void;
}) => {
  const { getNotasByEmpresa } = useNotasDuplicadas();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: [
      'notasDuplicadasEmpresa',
      {
        codi_emp: empresa?.external_id,
        date_start: range.data_ini,
        date_end: range.data_fim,
      },
    ],
    queryFn: getNotasByEmpresa,
    enabled: !!empresa,
    staleTime: 60 * 1000,
  });

  const notas = data?.data || [];
  const periodo = useMemo(
    () => ({ inicio: range.data_ini, fim: range.data_fim }),
    [range.data_ini, range.data_fim],
  );

  const abrirTela = () => {
    if (!empresa) return;
    navigate({
      pathname: `/modulesHome/integration/duplicateNotes/${empresa.external_id}`,
      search: createSearchParams({
        ...(range.data_ini ? { date_start: range.data_ini } : {}),
        ...(range.data_fim ? { date_end: range.data_fim } : {}),
      }).toString(),
    });
  };

  return (
    <Drawer isOpen={!!empresa} onClose={onClose} size="xl" placement="right">
      <DrawerOverlay bg="rgba(15,23,42,0.35)" backdropFilter="blur(4px)" />
      <DrawerContent bg="#F8FAFC" maxW="900px">
        <DrawerCloseButton />
        <DrawerHeader pb={3} borderBottomWidth="1px" borderColor="gray.200">
          <Flex direction="column" gap={1} pr={8}>
            <Flex align="center" gap={2}>
              <Copy size={20} weight="duotone" color="#b45309" />
              <Text fontSize="17px" fontWeight={700}>
                Notas Duplicadas
              </Text>
              <Button
                ml="auto"
                size="xs"
                variant="ghost"
                colorScheme="orange"
                rightIcon={<ArrowSquareOut size={13} weight="bold" />}
                onClick={abrirTela}
                isDisabled={!empresa}
              >
                Abrir na tela de duplicadas
              </Button>
            </Flex>
            {empresa && (
              <Flex
                gap={3}
                align="center"
                fontSize="12px"
                color="#64748B"
                fontWeight={500}
                wrap="wrap"
              >
                <Text>ID: {empresa.external_id}</Text>
                <Text>·</Text>
                <Text fontWeight={600} color="#334155">
                  {empresa.razao_social || '—'}
                </Text>
                <Text>·</Text>
                <Text fontFamily="mono">{empresa.cnpj || '—'}</Text>
              </Flex>
            )}
          </Flex>
        </DrawerHeader>
        <DrawerBody px={4} py={4}>
          {isLoading && (
            <Flex justify="center" py={12}>
              <Spinner color="#b45309" />
            </Flex>
          )}

          {!isLoading && notas.length === 0 && (
            <Flex justify="center" py={12}>
              <Text color="#94A3B8">Nenhuma nota duplicada encontrada neste período.</Text>
            </Flex>
          )}

          {!isLoading && notas.length > 0 && <GruposDuplicadas notas={notas} periodo={periodo} />}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
